import { QueryFunctionContext } from '@tanstack/react-query';

import { GET_WITH_CREDENTIALS } from '~/api';
import { addListToQueryParams } from '~/api/helpers';
import { JiraRequestByIdUrl, JiraRequestOpenIssuesCountUrl, JiraRequestsApiUrl } from '~/api/urls';
import { EHttpStatus } from '~/domain/EHttpStatus';
import { IBackendJiraConfig } from '~/domain/IBackendConfig';
import { IJiraIssue } from '~/domain/IJiraIssue';
import { JiraList } from '~/domain/JiraList';
import { ResponseError } from '~/domain/ResponseError';

const maxResults = 10;
export const getRequests = async (
  context: QueryFunctionContext<[string, { market: string, status?: string[], search?: string, jiraProjectConfig: IBackendJiraConfig }], { startAt: number; }>
) => {

  const { market, status = [], search } = context.queryKey[1];

  const { startAt } = context.pageParam || { startAt: 0 };
  const baseUrl = JiraRequestsApiUrl.expand({
    startAt,
    maxResults,
    market,
    search,
  });
  const url = addListToQueryParams('status', baseUrl, status);
  const resources = await GET_WITH_CREDENTIALS<JiraList<{ issues: IJiraIssue[] }>>(url);
  return resources || {} as JiraList<{ issues: IJiraIssue[] }>;
}

export const getRequest = async (context: QueryFunctionContext<[string, { requestId: string, market: string, jiraProjectConfig: IBackendJiraConfig }]>): Promise<IJiraIssue> => {
  const { requestId } = context.queryKey[1];

  const url = JiraRequestByIdUrl.expand({
    requestId,
  });

  try {
    const jiraIssue = await GET_WITH_CREDENTIALS<IJiraIssue>(url) || {} as IJiraIssue;
    if (!jiraIssue) {
      throw new ResponseError(EHttpStatus.NotFound, `Request ${requestId} not found`, '');
    }

    return jiraIssue;
  } catch (e) {
    if ((e as ResponseError).status !== EHttpStatus.BadRequest) {
      throw e;
    }
    throw new ResponseError(EHttpStatus.NotFound, `Request ${requestId} not found`, '');
  }
}

export const getRequestOpenIssueCount = async (context: QueryFunctionContext<[string, { requestId: string }]>): Promise<number | null> => {
  const { requestId } = context.queryKey[1];
  const url = JiraRequestOpenIssuesCountUrl.expand({
    requestId,
  });

  try {
    const totalOpenIssues = await GET_WITH_CREDENTIALS<number>(url) || 0;
    return totalOpenIssues;
  } catch (e) {
    const error = e as ResponseError;
    // Suppress 400 and 404 errors, return null for these cases
    if (error.status === EHttpStatus.BadRequest || error.status === EHttpStatus.NotFound) {
      return null;
    }
    
    // For all other errors, also return null to avoid breaking the UI
    return null;
  }
}
